import { cloneEditorValue, normalizeClassList, sanitizeClassDefinitions } from './htmlEditorUtils.js';

export const FONT_THEME_CLASS = 'portfolio-font-theme';
export const HEADING_FONT_PROPERTY = '--portfolio-heading-font';
export const FONT_BREAKPOINTS = ['base', 'sm', 'md', 'lg', 'xl'];

const FALLBACKS = {
  sans: 'Arial, sans-serif',
  serif: 'Georgia, serif',
  mono: 'Consolas, monospace',
  display: 'Arial, sans-serif',
};

const group = (category, families, weights = [400, 700]) => families.map((family) => ({
  family,
  category,
  weights,
  fallback: FALLBACKS[category],
}));

// A local catalog keeps the picker reliable and prevents document data from
// becoming a Google Fonts URL. Only these known families can be downloaded.
export const GOOGLE_FONT_CATALOG = [
  ...group('sans', [
    'Albert Sans', 'Archivo', 'Assistant', 'Atkinson Hyperlegible', 'Barlow',
    'Barlow Condensed', 'Be Vietnam Pro', 'Cabin', 'Cairo', 'Catamaran', 'Chivo',
    'Comfortaa', 'Commissioner', 'DM Sans', 'Dosis', 'Exo 2', 'Figtree', 'Hind',
    'IBM Plex Sans', 'Inter', 'Josefin Sans', 'Kanit', 'Karla', 'Lato', 'Lexend',
    'Manrope', 'Montserrat', 'Mulish', 'Nanum Gothic', 'Noto Sans',
    'Noto Sans Display', 'Nunito', 'Nunito Sans', 'Open Sans', 'Oswald', 'Outfit',
    'Overpass', 'Oxygen', 'Plus Jakarta Sans', 'Poppins', 'Prompt', 'Quicksand',
    'Raleway', 'Roboto', 'Roboto Condensed', 'Rubik', 'Source Sans 3',
    'Space Grotesk', 'Ubuntu', 'Work Sans',
  ]),
  ...group('serif', [
    'Alegreya', 'Bitter', 'Bodoni Moda', 'Cardo', 'Cormorant Garamond',
    'Crimson Pro', 'Domine', 'EB Garamond', 'Fraunces', 'IBM Plex Serif',
    'Libre Baskerville', 'Libre Caslon Text', 'Lora', 'Merriweather', 'Noto Serif',
    'Noto Serif Display', 'Old Standard TT', 'Petrona', 'Playfair Display',
    'PT Serif', 'Roboto Serif', 'Source Serif 4', 'Spectral', 'Vollkorn',
  ]),
  ...group('mono', [
    'Anonymous Pro', 'Azeret Mono', 'Cousine', 'Fira Code', 'IBM Plex Mono',
    'Inconsolata', 'JetBrains Mono', 'Noto Sans Mono', 'Roboto Mono',
    'Source Code Pro', 'Space Mono',
  ]),
  ...group('display', ['Cinzel', 'Fredoka', 'League Spartan']),
  ...group('display', [
    'Abril Fatface', 'Anton', 'Bebas Neue', 'Bungee', 'Fjalla One', 'Lilita One',
    'Lobster', 'Pacifico', 'Permanent Marker', 'Righteous', 'Staatliches',
  ], [400]),
];

const FONT_BY_NAME = new Map(
  GOOGLE_FONT_CATALOG.map((font) => [font.family.toLowerCase(), font])
);

export const getGoogleFont = (familyOrStack = '') => {
  const firstFamily = String(familyOrStack || '')
    .split(',')[0]
    .trim()
    .replace(/^['"]|['"]$/g, '');
  return FONT_BY_NAME.get(firstFamily.toLowerCase()) || null;
};

export const googleFontStack = (familyOrFont) => {
  const font = typeof familyOrFont === 'string' ? getGoogleFont(familyOrFont) : familyOrFont;
  return font ? `"${font.family}", ${font.fallback}` : '';
};

export const normalizeGoogleFontValue = (value, { preserveUnknown = true } = {}) => {
  const font = getGoogleFont(value);
  if (font) return googleFontStack(font);
  return preserveUnknown ? String(value || '') : '';
};

export const buildGoogleFontsUrl = (families = []) => {
  const fonts = [...new Map(families
    .map((entry) => getGoogleFont(entry?.family || entry))
    .filter(Boolean)
    .map((font) => [font.family, font])).values()]
    .sort((left, right) => left.family.localeCompare(right.family));

  if (!fonts.length) return '';
  const familyQueries = fonts.map((font) => {
    const family = encodeURIComponent(font.family).replace(/%20/g, '+');
    return `family=${family}:wght@${font.weights.join(';')}`;
  });
  return `https://fonts.googleapis.com/css2?${familyQueries.join('&')}&display=swap`;
};

export const collectGoogleFontsFromClasses = (classes = {}) => {
  const families = new Map();
  Object.values(sanitizeClassDefinitions(classes)).forEach((definition) => {
    FONT_BREAKPOINTS.forEach((breakpoint) => {
      const styles = definition?.[breakpoint] || {};
      [styles.fontFamily, styles[HEADING_FONT_PROPERTY]].forEach((value) => {
        const font = getGoogleFont(value);
        if (font) families.set(font.family, font);
      });
    });
  });
  return [...families.values()].sort((left, right) => left.family.localeCompare(right.family));
};

export const getPortfolioFontTheme = (editorDocument = {}) => {
  const base = editorDocument?.classes?.[FONT_THEME_CLASS]?.base || {};
  return {
    body: getGoogleFont(base.fontFamily)?.family || '',
    headings: getGoogleFont(base[HEADING_FONT_PROPERTY])?.family || '',
  };
};

const emptyDefinition = () => ({ base: {}, sm: {}, md: {}, lg: {}, xl: {} });

export const applyPortfolioFontTheme = (editorDocument, theme = {}) => {
  const next = cloneEditorValue(editorDocument);
  next.classes = { ...(next.classes || {}) };
  next.root.attributes = { ...(next.root.attributes || {}) };

  // Typography is document-level CSS. Remove the class used by the first
  // implementation so choosing a font never marks a content element.
  const rootClasses = normalizeClassList(next.root.attributes.class)
    .filter((className) => className !== FONT_THEME_CLASS);
  if (rootClasses.length) next.root.attributes.class = rootClasses;
  else delete next.root.attributes.class;

  const body = googleFontStack(theme.body);
  const headings = googleFontStack(theme.headings);
  if (!body && !headings) {
    delete next.classes[FONT_THEME_CLASS];
    return next;
  }

  const definition = emptyDefinition();
  if (body) definition.base.fontFamily = body;
  if (headings) definition.base[HEADING_FONT_PROPERTY] = headings;
  next.classes[FONT_THEME_CLASS] = definition;
  return next;
};

export const detachFontThemeFromRoot = (editorDocument) => {
  const rootClasses = normalizeClassList(editorDocument?.root?.attributes?.class);
  if (!rootClasses.includes(FONT_THEME_CLASS)) return editorDocument;

  const next = cloneEditorValue(editorDocument);
  const remaining = rootClasses.filter((className) => className !== FONT_THEME_CLASS);
  if (remaining.length) next.root.attributes.class = remaining;
  else delete next.root.attributes.class;
  return next;
};

export const normalizeAiFontDefinitions = (editorDocument) => {
  const next = cloneEditorValue(editorDocument);
  const acceptedFamilies = new Set();
  Object.values(next.classes || {}).forEach((definition) => {
    FONT_BREAKPOINTS.forEach((breakpoint) => {
      const styles = definition?.[breakpoint];
      if (!styles) return;
      if (Object.prototype.hasOwnProperty.call(styles, 'fontFamily')) {
        const font = getGoogleFont(styles.fontFamily);
        if (font && (acceptedFamilies.has(font.family) || acceptedFamilies.size < 2)) {
          acceptedFamilies.add(font.family);
          styles.fontFamily = googleFontStack(font);
        }
        else delete styles.fontFamily;
      }
      if (Object.prototype.hasOwnProperty.call(styles, HEADING_FONT_PROPERTY)) {
        const font = getGoogleFont(styles[HEADING_FONT_PROPERTY]);
        if (font && (acceptedFamilies.has(font.family) || acceptedFamilies.size < 2)) {
          acceptedFamilies.add(font.family);
          styles[HEADING_FONT_PROPERTY] = googleFontStack(font);
        }
        else delete styles[HEADING_FONT_PROPERTY];
      }
    });
  });

  return detachFontThemeFromRoot(next);
};

export const generatePortfolioFontThemeCss = (classes = {}) => {
  const bodyFont = classes?.[FONT_THEME_CLASS]?.base?.fontFamily;
  const headingFont = classes?.[FONT_THEME_CLASS]?.base?.[HEADING_FONT_PROPERTY];
  const declarations = [];
  if (getGoogleFont(bodyFont)) declarations.push(`  font-family: ${googleFontStack(bodyFont)};`);
  if (getGoogleFont(headingFont)) declarations.push(`  ${HEADING_FONT_PROPERTY}: ${googleFontStack(headingFont)};`);
  if (!declarations.length) return '';

  const blocks = [`.html-editor-canvas {\n${declarations.join('\n')}\n}`];
  if (getGoogleFont(headingFont)) {
    blocks.push(`.html-editor-canvas :where(h1, h2, h3, h4, h5, h6) {\n  font-family: var(${HEADING_FONT_PROPERTY}, inherit);\n}`);
  }
  return blocks.join('\n\n');
};

export const withoutFontThemeDefinition = (classes = {}) => Object.fromEntries(
  Object.entries(classes).filter(([className]) => className !== FONT_THEME_CLASS)
);

export const GOOGLE_FONT_AI_CONTEXT = GOOGLE_FONT_CATALOG.map((font) => font.family).join(', ');
